import { ReactNode, useLayoutEffect, useMemo, useState } from 'react'
import { HomeContext } from './home-context'
import { useApiGetStore } from 'core/api/store/useApiGetStore'
import { Store } from 'components/common/filters/select-store'
import { useApiGetTemplates } from 'core/api/template/useApiGetTemplates'
import { Visibility } from 'core/models/template'
import { useApiMenuGetUserPreferences } from 'core/api/menu/useApiMenuGetUserPreferences'
import { useHomeFiltersUrlSync } from 'core/store/useHomeFiltersUrlSync'
import { VpLoader } from '@vtmn-play/react'
import { useApiGetTeams } from 'core/api/team/useApiGetTeams'
import { Team } from '../../../components/common/filters2/filters/select-team'
import { useGlobalFiltersStore } from '../../../core/store/useGlobalFiltersStore'
import { useHomeFiltersStore } from '../../../core/store/usehomeFiltersStore'

interface HomeProviderProps {
  children: ReactNode
}

export const HomeProvider = ({ children }: HomeProviderProps) => {
  const storeId = useGlobalFiltersStore(state => state.storeId)
  const channel = useHomeFiltersStore(state => state.channel)
  const aggregation = useHomeFiltersStore(state => state.aggregation)
  const teamId = useHomeFiltersStore(state => state.teamId)
  const syncHomeWithUserPreferences = useHomeFiltersStore(state => state.syncWithUserPreferences)
  const syncGlobalWithUserPreferences = useGlobalFiltersStore(state => state.syncWithUserPreferences)
  const setStoreId = useGlobalFiltersStore(state => state.setStoreId)
  const setChannel = useHomeFiltersStore(state => state.setChannel)
  const setAggregation = useHomeFiltersStore(state => state.setAggregation)
  const setTeamId = useHomeFiltersStore(state => state.setTeamId)
  const [isFiltersInitialized, setIsFiltersInitialized] = useState(false)

  useHomeFiltersUrlSync()

  const { data: store, isFetched: areStoresFetched } = useApiGetStore(storeId)
  const { data: teams, isFetched: areTeamsFetched } = useApiGetTeams(
    { storeId },
    { enabled: !!storeId && isFiltersInitialized }
  )
  const { data: user, isLoading } = useApiMenuGetUserPreferences()
  const { data: templates } = useApiGetTemplates()

  // Ensure storeId from stores
  const safeStoreId = areStoresFetched && storeId !== null && !store ? null : storeId
  const team: Team = teams?.find(team => team.id === teamId) || null
  const hasResolvedTeams = areTeamsFetched || teams !== undefined
  // Ensure teamId from store teams
  const safeTeamId = hasResolvedTeams && teamId !== null && !team ? null : teamId

  // Sync user preferences before anything else
  useLayoutEffect(() => {
    if (isLoading || isFiltersInitialized) {
      return
    }

    syncGlobalWithUserPreferences(user)
    syncHomeWithUserPreferences(user)
    setIsFiltersInitialized(true)
  }, [isLoading, isFiltersInitialized, syncGlobalWithUserPreferences, syncHomeWithUserPreferences, user])

  const visibleTemplates = useMemo(
    () => templates?.[Visibility.IsVisible]?.map(it => ({ id: it.id, label: it.label })) ?? [],
    [templates]
  )

  // Keep store consistent as soon as stores are known.
  useLayoutEffect(() => {
    if (safeStoreId === null && storeId !== null) {
      setStoreId(user?.preferences?.default_filters?.store_id ?? null)
    }
  }, [safeStoreId, storeId, setStoreId, user?.preferences?.default_filters?.store_id])

  // Keep store consistent as soon as teams are known.
  useLayoutEffect(() => {
    if (safeTeamId === null && teamId !== null) {
      setTeamId(null)
    }
  }, [safeTeamId, teamId, setTeamId])

  return (
    <HomeContext.Provider
      value={{
        channel,
        aggregation,
        storeId: safeStoreId,
        teamId: safeTeamId,
        store: store as Store,
        team,
        templates: visibleTemplates,
        setChannel,
        setAggregation,
        setTeamId,
        userFirstName: user?.user_name
      }}
    >
      {isLoading || !isFiltersInitialized ? <VpLoader {...({ size: 'extra-large' } as any)} /> : children}
    </HomeContext.Provider>
  )
}
